import { Show, createMemo, createResource, createSignal } from 'solid-js';
import { useApi, isOk } from '../api';
import { firstError } from '../api/envelope';
import Text from '../components/Text';
import ItemIdentifierPicker from '../components/ItemIdentifierPicker';
import { SkeletonCard } from '../components/Skeleton';
import { useToast } from '../components/MessageBox';
import { useTranslation } from '../hooks/useTranslation';

const sameItems = (left: string[], right: string[]) => {
    const sortedLeft = [...left].sort();
    const sortedRight = [...right].sort();
    return sortedLeft.length === sortedRight.length
        && sortedLeft.every((identifier, index) => identifier === sortedRight[index]);
};

export default function AdminCompressedPlacement() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();

    const [selectedIdentifiers, setSelectedIdentifiers] = createSignal<string[]>([]);
    const [submitting, setSubmitting] = createSignal(false);

    const [savedIdentifiers, { mutate: setSavedIdentifiers }] = createResource(async (): Promise<string[]> => {
        try {
            const res = await api.compressedPlacementAdmin.getBlockedItems();
            if (isOk(res.data)) {
                const identifiers = [...(res.data.value.itemIdentifiers ?? [])];
                setSelectedIdentifiers(identifiers);
                return identifiers;
            }
            toast?.error(firstError(res.data) ?? t('COMPRESSED_PLACEMENT_FETCH_FAILED'));
        } catch (e) {
            toast?.error((e as Error)?.message ?? t('COMPRESSED_PLACEMENT_FETCH_FAILED'));
        }
        return [];
    });

    const loading = () => savedIdentifiers.state === 'pending';
    const dirty = createMemo(() => !sameItems(selectedIdentifiers(), savedIdentifiers() ?? []));

    const submit = async () => {
        setSubmitting(true);
        try {
            const res = await api.compressedPlacementAdmin.setBlockedItems({ itemIdentifiers: selectedIdentifiers() });
            if (isOk(res.data)) {
                const identifiers = [...(res.data.value.itemIdentifiers ?? [])];
                setSavedIdentifiers(identifiers);
                setSelectedIdentifiers(identifiers);
                toast?.info(t('COMPRESSED_PLACEMENT_SAVED'));
            } else {
                toast?.error(firstError(res.data) ?? t('COMPRESSED_PLACEMENT_SAVE_FAILED'));
            }
        } catch (e) {
            toast?.error((e as Error)?.message ?? t('COMPRESSED_PLACEMENT_SAVE_FAILED'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div class="dashboard-page">
            <h1><Text val="COMPRESSED_PLACEMENT_TITLE" /></h1>

            <Show when={!loading()} fallback={<SkeletonCard />}>
                <form class="fcard v" onSubmit={(e) => { e.preventDefault(); void submit(); }} style={{ gap: 'var(--spacing-md)' }}>
                    <p style={{ margin: 0, 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                        <Text val="COMPRESSED_PLACEMENT_INTRO" />
                    </p>

                    <ItemIdentifierPicker
                        label={t('COMPRESSED_PLACEMENT_FIELD_ITEMS')}
                        placeholder={t('COMPRESSED_PLACEMENT_FIELD_PLACEHOLDER')}
                        value={selectedIdentifiers()}
                        onChange={setSelectedIdentifiers}
                        disabled={submitting()}
                        dedupe
                    />

                    <div class="h" style={{ 'justify-content': 'flex-end', gap: 'var(--spacing-sm)' }}>
                        <button type="button" class="btn btn-secondary" disabled={!dirty() || submitting()}
                            onClick={() => setSelectedIdentifiers(savedIdentifiers() ?? [])}>
                            <Text val="CANCEL" />
                        </button>
                        <button type="submit" class="btn btn-primary" disabled={!dirty() || submitting()}>
                            <Show when={submitting()} fallback={<Text val="SAVE" />}>
                                <i class="fa-solid fa-spinner fa-spin" />
                            </Show>
                        </button>
                    </div>
                </form>
            </Show>
        </div>
    );
}
